import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { ArrowBack as BackIcon, Download as DownloadIcon, History as LensIcon } from "@mui/icons-material";
import { useAppDispatch } from "../../store/hooks";
import { setSnackAction } from "../../store/slices/snackSlice";
import lensService, { type FindingDecision, type Run, type Severity } from "../../services/lensService";
import useRun from "./hooks/useRun";
import useLensMutation from "./hooks/useLensMutation";
import FindingCard from "./FindingCard";
import RunDecisions from "./RunDecisions";
import RunSideBySide from "./RunSideBySide";
import {
    type DecisionFilter,
    SEVERITIES,
    apiErrorMessage,
    formatDuration,
    formatElapsed,
    formatWhen,
    matchesFilters,
    plural,
    runBy,
    severityColour,
} from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3) },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    headerTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    meta: { display: "flex", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap", color: "#777", marginBottom: theme.spacing(4) },
    spacer: { flexGrow: 1 },
    summary: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
    showing: { color: "#777", marginBottom: theme.spacing(2) },
    view: { marginBottom: theme.spacing(2) },
    progressMeta: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(4),
        marginTop: theme.spacing(2),
        color: "#777",
    },
    elapsed: { fontVariantNumeric: "tabular-nums" },
}));

interface RunSummaryLineProps {
    run: Run;
    severityFilter: Severity | null;
    // Absent: there are no findings to filter, so the chips are plain counts.
    onSeverityFilter?: (severity: Severity | null) => void;
}

const RunSummaryLine = ({ run, severityFilter, onSeverityFilter }: RunSummaryLineProps) => {
    const { classes } = useStyles();
    const { counts } = run;
    const countOf = (severity: Severity) =>
        onSeverityFilter ? run.findings.filter((finding) => matchesFilters(finding, severity, null)).length : counts[severity];
    return (
        <Paper variant="outlined" className={classes.summary}>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle1">{plural(counts.total, "finding")}</Typography>
                {SEVERITIES.map((severity) => {
                    const count = countOf(severity);
                    if (count === 0) return null;
                    if (!onSeverityFilter)
                        return <Chip key={severity} size="small" color={severityColour[severity]} label={`${count} ${severity}`} />;
                    const active = severityFilter === severity;
                    return (
                        <Chip
                            key={severity}
                            size="small"
                            clickable
                            variant={active ? "filled" : "outlined"}
                            color={severityColour[severity]}
                            label={`${count} ${severity}`}
                            aria-pressed={active}
                            onClick={() => onSeverityFilter(active ? null : severity)}
                        />
                    );
                })}
            </Box>
            <Typography variant="body2" style={{ color: "#777", marginTop: 4 }}>
                {counts.omissions > 0 ? `${plural(counts.omissions, "omission")} · ` : ""}
                {counts.notAssessed > 0 ? `${counts.notAssessed} not assessed · ` : ""}
                {plural(run.mappedProvisions.length, "provision")} located · {plural(run.sectionCount, "section")}
            </Typography>
        </Paper>
    );
};

// useRun refetches while the run is running, so the stage moves on by itself.
const RunProgress = ({ run }: { run: Run }) => {
    const { classes } = useStyles();
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const startedAt = new Date(run.startedOn).getTime();

    return (
        <Paper className={classes.summary} role="status">
            <Typography variant="body2" gutterBottom>
                {run.progress ?? "Reviewing with AI"}
            </Typography>
            <LinearProgress />
            <Box className={classes.progressMeta}>
                <Typography variant="caption">This can take a few minutes; you can leave this page.</Typography>
                {!isNaN(startedAt) && (
                    <Typography variant="caption" className={classes.elapsed}>
                        {formatElapsed(now - startedAt)}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

const RunDetail = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const { id } = useParams<{ id: string }>();
    const run = useRun(id);
    const decide = useLensMutation(lensService.decide);
    const generateRedline = useMutation({ mutationFn: lensService.generateRedline });
    const [decidingKey, setDecidingKey] = useState<string | null>(null);
    const [severityFilter, setSeverityFilter] = useState<Severity | null>(null);
    const [decisionFilter, setDecisionFilter] = useState<DecisionFilter | null>(null);
    const [view, setView] = useState<"list" | "side">("list");

    const current = run.data;
    const notFound = run.isError && isAxiosError(run.error) && run.error.response?.status === 404;

    const back = (
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate("/lens/runs")}>
            All runs
        </Button>
    );

    if (!id || !current) {
        return (
            <Box className={classes.root}>
                <Box className={classes.header}>
                    <Box className={classes.headerTitle}>
                        <LensIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                        <Typography variant="h4">{notFound ? "Run not found" : "Run"}</Typography>
                    </Box>
                    {back}
                </Box>
                <Paper>
                    {run.isLoading ? (
                        <Box className={classes.center}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Typography variant="body1" className={classes.empty}>
                            {notFound ? "No run was found at this address. It may have been deleted." : "This run could not be loaded."}
                        </Typography>
                    )}
                </Paper>
            </Box>
        );
    }

    const isUpload = current.source === "upload";
    const findings = current.findings ?? [];
    const isFiltered = severityFilter !== null || decisionFilter !== null;
    const shown = findings.filter((finding) => matchesFilters(finding, severityFilter, decisionFilter));

    const download = (which: "original" | "markedUp") =>
        (which === "original" ? lensService.downloadOriginal(current) : lensService.downloadMarkedUp(current)).catch(() =>
            dispatch(
                setSnackAction(
                    `Could not download the ${which === "original" ? "original" : "marked-up copy"}. Please try again.`,
                    "error",
                ),
            ),
        );

    const onDecide = (findingKey: string, decision: FindingDecision) => {
        setDecidingKey(findingKey);
        decide.mutate({ identifier: id, findingKey, decision }, { onSettled: () => setDecidingKey(null) });
    };

    const generate = () =>
        generateRedline.mutate(id, {
            onSuccess: (updated) =>
                dispatch(
                    setSnackAction(
                        updated.redline.status === "commentsonly"
                            ? "Marked-up copy generated with comments only — the tracked changes could not be written."
                            : "Marked-up copy generated from the current decisions.",
                        updated.redline.status === "commentsonly" ? "warning" : "success",
                    ),
                ),
            onError: (error) => dispatch(setSnackAction(apiErrorMessage(error, "Could not generate the marked-up copy."), "error")),
            // A failed attempt is recorded on the run, so reload it either way.
            onSettled: () => queryClient.invalidateQueries({ queryKey: ["lens"] }),
        });

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.headerTitle}>
                    <LensIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                    <Typography variant="h4">{current.documentName}</Typography>
                </Box>
                {back}
            </Box>
            <Box className={classes.meta}>
                {current.isTest && <Chip size="small" variant="outlined" color="secondary" label="Test run" />}
                {!isUpload && <Chip size="small" variant="outlined" label="Word add-in" />}
                <Typography variant="body2">
                    {current.clientName ? `${current.clientName} · ` : ""}
                    {current.playbookName} v{current.playbookVersion} · {formatWhen(current.startedOn)} · Run by {runBy(current)} ·{" "}
                    {formatDuration(current.durationMs)}
                </Typography>
                <span className={classes.spacer} />
                {isUpload && current.status === "complete" && (
                    <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => download("original")}>
                        Original
                    </Button>
                )}
            </Box>

            {current.status === "running" && <RunProgress run={current} />}

            {current.status === "failed" && <Alert severity="error">This run failed: {current.error}</Alert>}

            {current.status === "complete" && (
                <>
                    <RunSummaryLine
                        run={current}
                        severityFilter={severityFilter}
                        onSeverityFilter={isUpload && findings.length > 0 ? setSeverityFilter : undefined}
                    />
                    {(!isUpload || findings.length > 0) && (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={view}
                            onChange={(_e, value: "list" | "side" | null) => value && setView(value)}
                            aria-label="Findings view"
                            className={classes.view}
                        >
                            <ToggleButton value="list">List</ToggleButton>
                            <ToggleButton value="side">Side by side</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {!isUpload && view === "side" ? (
                        <RunSideBySide run={current} findings={findings} decidingKey={decidingKey} onDecide={onDecide} />
                    ) : !isUpload ? (
                        <Typography variant="body2" style={{ color: "#777" }}>
                            The findings stay in the client&rsquo;s Word document. Lens keeps a summary of this review only.
                        </Typography>
                    ) : findings.length === 0 ? (
                        <Paper>
                            <Typography variant="body1" className={classes.empty}>
                                No findings were raised against this document.
                            </Typography>
                        </Paper>
                    ) : (
                        <>
                            <RunDecisions
                                run={current}
                                isGenerating={generateRedline.isPending}
                                onGenerate={generate}
                                onDownload={() => download("markedUp")}
                                decisionFilter={decisionFilter}
                                onDecisionFilter={setDecisionFilter}
                            />
                            {isFiltered && (
                                <Typography variant="body2" className={classes.showing}>
                                    Showing {shown.length} of {plural(findings.length, "finding")}
                                </Typography>
                            )}
                            {view === "side" && (
                                <RunSideBySide run={current} findings={shown} decidingKey={decidingKey} onDecide={onDecide} />
                            )}
                            {view === "list" && isFiltered && shown.length === 0 && (
                                <Paper>
                                    <Typography variant="body1" className={classes.empty}>
                                        No findings match the selected filters.
                                    </Typography>
                                    <Box display="flex" justifyContent="center" paddingBottom={3}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setSeverityFilter(null);
                                                setDecisionFilter(null);
                                            }}
                                        >
                                            Clear filters
                                        </Button>
                                    </Box>
                                </Paper>
                            )}
                            {view === "list" &&
                                shown.map((finding) => (
                                    <FindingCard
                                        key={finding.key}
                                        finding={finding}
                                        isDeciding={decidingKey === finding.key}
                                        onDecide={(decision) => onDecide(finding.key, decision)}
                                    />
                                ))}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default RunDetail;
